import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";
import { articleCards } from "@/mocks/articles";

const faqItems = [
  {
    q: "Is Sugargoo legit?",
    a: "Yes, Sugargoo is a legitimate shopping agent platform that has been operating for several years. The platform connects international buyers with Chinese marketplaces, offering warehouse storage, quality inspection, and international shipping. Thousands of users rely on Sugargoo daily for their shopping needs, and the platform maintains transparent pricing with no hidden fees.",
  },
  {
    q: "What is Sugargoo Spreadsheet?",
    a: "Sugargoo Spreadsheet is a structured product discovery system that organizes curated finds into searchable categories. Instead of manually browsing endless product listings, users can use the spreadsheet to quickly locate trending items across categories like shoes, clothing, accessories, and more. It serves as a time-saving database for discovering popular products efficiently.",
  },
  {
    q: "How does Sugargoo warehouse work?",
    a: "When you purchase items through Sugargoo, they are shipped to the Sugargoo warehouse first. The warehouse team performs quality checks, takes inspection photos, and stores your items securely. You can accumulate multiple purchases and then ship them together in a single parcel to save on international shipping costs. Most items can be stored for up to 180 days free of charge.",
  },
  {
    q: "How much is Sugargoo shipping?",
    a: "Sugargoo shipping costs vary based on several factors including package weight, dimensions, destination country, and shipping method selected. Air shipping tends to be faster but more expensive, while sea shipping offers lower rates for heavier packages. The platform provides a shipping calculator that estimates costs before you commit, so you can plan your budget accordingly.",
  },
  {
    q: "Does Sugargoo provide coupons?",
    a: "Yes, Sugargoo regularly offers coupons and discount codes for both new and returning users. These include welcome discounts for first-time shoppers, seasonal promotional codes, and shipping fee reductions. Coupons can typically be applied during checkout. Following the Sugargoo Spreadsheet database helps you stay updated on the latest available discounts.",
  },
  {
    q: "How can I use Sugargoo Spreadsheet?",
    a: "Using the Sugargoo Spreadsheet is straightforward. Simply browse the organized categories to find products you are interested in, click through to view detailed listings, compare items across different sellers, and use the information provided to make informed purchasing decisions. The spreadsheet is designed to be beginner-friendly and requires no technical knowledge.",
  },
];

export default function ContentSection() {
  return (
    <>
      {/* What Is Sugargoo Spreadsheet */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <h2 className="text-2xl md:text-3xl text-foreground-900">
              Everything You Need To Know About Sugargoo Spreadsheet
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              Sugargoo Spreadsheet is a structured product discovery system that helps shoppers find popular products faster. Instead of spending hours searching manually, users can explore organized categories, compare products, check buying information and discover trending finds all in one place.
            </p>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              The spreadsheet serves as a comprehensive database that connects you directly to thousands of curated items across fashion, accessories, and lifestyle categories. Whether you are a first-time Sugargoo user or an experienced shopper, the organized layout eliminates the guesswork from product discovery.
            </p>

            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-background-300">
                    <th className="text-left py-3 pr-4 font-semibold text-foreground-800 whitespace-nowrap">Method</th>
                    <th className="text-left py-3 pr-4 font-semibold text-foreground-800 whitespace-nowrap">Search Speed</th>
                    <th className="text-left py-3 pr-4 font-semibold text-foreground-800 whitespace-nowrap">Organization</th>
                    <th className="text-left py-3 font-semibold text-foreground-800 whitespace-nowrap">Beginner Friendly</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 font-medium text-primary-600 whitespace-nowrap">Sugargoo Spreadsheet</td>
                    <td className="py-3 pr-4 text-accent-600 whitespace-nowrap">&#9733;&#9733;&#9733;&#9733;&#9733;</td>
                    <td className="py-3 pr-4 text-foreground-700 whitespace-nowrap">Excellent</td>
                    <td className="py-3 text-accent-600 font-medium whitespace-nowrap">Yes</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 font-medium text-foreground-800 whitespace-nowrap">Manual Searching</td>
                    <td className="py-3 pr-4 text-foreground-500 whitespace-nowrap">&#9733;&#9733;</td>
                    <td className="py-3 pr-4 text-foreground-700 whitespace-nowrap">Poor</td>
                    <td className="py-3 text-foreground-700 whitespace-nowrap">Difficult</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-foreground-800 whitespace-nowrap">Random Links</td>
                    <td className="py-3 pr-4 text-foreground-500 whitespace-nowrap">&#9733;</td>
                    <td className="py-3 pr-4 text-foreground-700 whitespace-nowrap">Low</td>
                    <td className="py-3 text-foreground-700 whitespace-nowrap">No</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20desk%20setup%20with%20laptop%20displaying%20organized%20spreadsheet%20interface%20surrounded%20by%20curated%20fashion%20accessories%20and%20fabric%20swatches%20in%20warm%20sage%20and%20sand%20tones%2C%20soft%20natural%20window%20light%2C%20editorial%20productivity%20photography%2C%20clean%20Scandinavian%20aesthetic%2C%20elegant%20composition%20with%20copy%20space&width=800&height=600&seq=sugargoo-spreadsheet-preview-v02&orientation=landscape"
              alt="Sugargoo Spreadsheet organization preview"
              title="Sugargoo Spreadsheet - Organized Product Discovery Database"
              className="w-full rounded-2xl"
            />
            <div className="absolute -bottom-3 -right-3 bg-accent-100 text-accent-800 text-xs font-semibold px-4 py-2 rounded-full">
              Updated Daily
            </div>
          </div>
        </div>
      </section>

      {/* Why Use Sugargoo Spreadsheet */}
      <section className="bg-background-100 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl text-foreground-900">
              Why Use Sugargoo Spreadsheet?
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Thousands of shoppers trust the Sugargoo Spreadsheet to streamline their product discovery and make smarter purchasing decisions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              {
                title: "Fast Product Discovery",
                desc: "Find products without wasting hours searching. The spreadsheet organizes thousands of items so you can locate what you need in moments, not hours.",
                icon: "ri-flashlight-line",
              },
              {
                title: "Organized Categories",
                desc: "Browse shoes, clothing, accessories and trending items all neatly arranged. Every category is clearly labeled for effortless navigation.",
                icon: "ri-stack-line",
              },
              {
                title: "Better Decision Making",
                desc: "Compare products before purchasing. With detailed listings and organized data, you can evaluate options side by side and choose wisely.",
                icon: "ri-bar-chart-line",
              },
              {
                title: "Beginner Friendly",
                desc: "Perfect for first-time users. No prior experience with shopping agents is necessary — the spreadsheet guides you through every step naturally.",
                icon: "ri-user-smile-line",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 hover:border-primary-200 transition-colors"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                  <i className={`${card.icon} text-accent-600 text-xl`}></i>
                </span>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">
                  {card.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sugargoo Review Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl text-foreground-900 text-center">
          Sugargoo Review &mdash; Is Sugargoo Legit?
        </h2>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto text-center">
          An honest, independent review of the Sugargoo platform covering the complete user experience, warehouse service, shipping options, and more.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          <div className="space-y-5">
            {[
              {
                title: "Platform Overview",
                content: "Sugargoo operates as a comprehensive shopping agent that bridges international buyers with Chinese marketplaces. The platform handles purchasing, warehousing, inspection, and worldwide shipping, making cross-border shopping accessible to everyone regardless of location.",
              },
              {
                title: "User Experience",
                content: "The platform interface is intuitive and well-organized. Users can easily search for products, place orders, track shipments, and manage their warehouse inventory from a single dashboard. First-time users will find the learning curve pleasantly manageable.",
              },
              {
                title: "Warehouse Service",
                content: "Sugargoo provides free warehouse storage for up to 180 days, which is generous compared to competitors. The quality inspection service includes detailed photos of each item so you can verify condition before international shipping.",
              },
              {
                title: "Customer Support",
                content: "Support is available through multiple channels including live chat and ticket system. Response times are generally reasonable, and the support team is knowledgeable about platform processes and shipping logistics.",
              },
            ].map((item) => (
              <div key={item.title}>
                <h3 className="text-base font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.content}</p>
              </div>
            ))}
          </div>

          <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70">
            <h3 className="text-lg font-semibold text-foreground-900 mb-5">Feature Ratings</h3>
            <div className="space-y-4">
              {[
                { label: "Warehouse Service", rating: "Excellent", stars: 5 },
                { label: "Product Management", rating: "Good", stars: 4 },
                { label: "Shipping Options", rating: "Good", stars: 4 },
                { label: "Beginner Experience", rating: "Excellent", stars: 5 },
                { label: "Customer Support", rating: "Good", stars: 4 },
                { label: "Value for Money", rating: "Excellent", stars: 5 },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-3 border-b border-background-200 last:border-none">
                  <span className="text-sm font-medium text-foreground-800">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i} className={`text-sm ${i < item.stars ? "text-yellow-500" : "text-background-300"}`}>
                          &#9733;
                        </span>
                      ))}
                    </span>
                    <span className="text-xs font-medium text-accent-600 ml-1">{item.rating}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/sugargoo-review"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Read Full Sugargoo Review
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </span>
          </Link>
        </div>
      </section>

      {/* Sugargoo Shipping Cost */}
      <section className="bg-background-100 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl text-foreground-900 text-center">
            Sugargoo Shipping Cost Explained
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto text-center">
            Understanding shipping costs is essential for budgeting your purchases. Here is a breakdown of the main shipping methods available through Sugargoo and what you can expect.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                method: "Air Shipping",
                speed: "Fast (7-15 days)",
                cost: "Moderate to High",
                desc: "Best balance of speed and cost for most packages. Ideal for orders under 5kg.",
                icon: "ri-plane-line",
              },
              {
                method: "Sea Shipping",
                speed: "Slow (30-60 days)",
                cost: "Low",
                desc: "Most economical option for heavy or bulk shipments. Patience required but significant savings.",
                icon: "ri-ship-line",
              },
              {
                method: "Express",
                speed: "Very Fast (3-7 days)",
                cost: "Highest",
                desc: "Premium courier services like DHL and FedEx for urgent deliveries with full tracking.",
                icon: "ri-rocket-line",
              },
            ].map((item) => (
              <div
                key={item.method}
                className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-4">
                  <i className={`${item.icon} text-accent-600 text-xl`}></i>
                </span>
                <h3 className="text-base font-semibold text-foreground-900">{item.method}</h3>
                <div className="mt-3 space-y-1.5">
                  <p className="text-sm text-foreground-600">
                    <span className="font-medium text-foreground-800">Speed:</span> {item.speed}
                  </p>
                  <p className="text-sm text-foreground-600">
                    <span className="font-medium text-foreground-800">Cost:</span> {item.cost}
                  </p>
                </div>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/sugargoo-shipping-cost"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Full Shipping Cost Guide
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Coupon & Discount */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="bg-accent-50 rounded-2xl p-8 md:p-10 border border-accent-200/60">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl text-foreground-900">
                Sugargoo Coupon &amp; Discount Center
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                Stay updated with the latest Sugargoo coupons, discount codes, and promotional offers. From welcome bonuses for new users to seasonal shipping discounts, we track the best available deals so you can save on every purchase.
              </p>
              <ul className="mt-5 space-y-2">
                {[
                  "Latest coupons updated regularly",
                  "Shipping discount codes available",
                  "Seasonal promotions and flash sales",
                  "Welcome bonuses for new users",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-foreground-700">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-accent-500 text-sm"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to="/sugargoo-coupon"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20"
            >
              View Current Discounts
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Warehouse Guide */}
      <section className="bg-background-100 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl text-foreground-900 text-center">
            Understanding Sugargoo Warehouse
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto text-center">
            The Sugargoo warehouse is where your purchases are stored before international shipping. Here is how the process works from purchase to delivery.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              {
                step: "01",
                title: "Item Arrival",
                desc: "Your purchased items arrive at the Sugargoo warehouse and are logged into the system with tracking updates.",
              },
              {
                step: "02",
                title: "QC Inspection",
                desc: "Warehouse staff inspect each item for defects and take detailed photos so you can verify quality.",
              },
              {
                step: "03",
                title: "Storage Period",
                desc: "Items are stored securely for up to 180 days free of charge while you accumulate more purchases.",
              },
              {
                step: "04",
                title: "Parcel Preparation",
                desc: "When ready, items are consolidated, carefully packed, and prepared for international shipment.",
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-primary-100 text-primary-600 text-lg font-bold mb-4">
                  {item.step}
                </span>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/sugargoo-warehouse"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Full Warehouse Guide
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Safety Information */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/70">
          <h2 className="text-xl md:text-2xl text-foreground-900 text-center">
            Sugargoo Safety Information
          </h2>
          <p className="mt-4 text-sm text-foreground-600 text-center max-w-2xl mx-auto leading-relaxed">
            This website provides informational resources only. We do not operate Sugargoo services. Users should always verify sellers, check product information, review QC photos, understand shipping rules, and make informed decisions before purchasing.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {["Verify Sellers", "Check Product Info", "Review QC Photos", "Understand Shipping Rules", "Make Informed Decisions"].map(
              (item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-700 bg-background-50 px-3 py-1.5 rounded-full border border-background-200"
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-shield-check-line text-accent-500 text-xs"></i>
                  </span>
                  {item}
                </span>
              )
            )}
          </div>
        </div>
      </section>

      {/* FAQ Schema */}
      <section className="bg-background-100 py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl text-foreground-900 text-center">
            Frequently Asked Questions
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 text-center">
            Everything you need to know about Sugargoo Spreadsheet and the Sugargoo platform.
          </p>

          <div className="mt-10 space-y-4">
            {faqItems.map((item, idx) => (
              <details
                key={idx}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                  <span className="text-sm font-semibold text-foreground-900 pr-4">{item.q}</span>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 group-open:rotate-180 transition-transform">
                    <i className="ri-arrow-down-s-line text-foreground-400"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 9 SEO Article Cards Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl text-foreground-900">
            Sugargoo Spreadsheet SEO Guides &amp; Resources
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Dive deeper into every aspect of Sugargoo with our comprehensive guides covering reviews, shipping, coupons, warehouses, safety, and more.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {articleCards.map((article) => (
            <Link
              key={article.slug}
              to={article.slug}
              className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer overflow-hidden flex flex-col"
            >
              <div className="w-full aspect-[8/5] overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - Sugargoo Spreadsheet SEO Article`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">
                    {article.readTime}
                  </span>
                </div>
                <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug mb-2">
                  {article.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed flex-1">
                  {article.excerpt}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 group-hover:gap-2 transition-all">
                  Read Article
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA to Main Site & Article */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="bg-primary-500 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl text-background-50">
            Ready to Explore Sugargoo Spreadsheet?
          </h2>
          <p className="mt-3 text-sm md:text-base text-background-50/85 max-w-xl mx-auto">
            Dive into our comprehensive database and discover thousands of curated products. Or read our in-depth guides to learn everything about Sugargoo.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold bg-background-50 text-primary-600 hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Sugargoo Spreadsheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
            <Link
              to="/sugargoo-spreadsheet-ultimate-guide"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold border-2 border-background-50/60 text-background-50 hover:bg-background-50 hover:text-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Read Ultimate Guide
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line text-xs"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}